import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { RoundedButton } from '../../../elements';
import { CircularProgress } from '@ann/web-components';
import { listDrives } from '../../../services/drives';
import { listFoldersByTenant } from '../../../services/folders';
import useStore from '../../../hooks/useStore';
import {
  ModalWrapper,
  ModalContent,
  ModalBody,
  ModalFooter,
  InputLabel,
  HorizontalInputWrapper,
  FlexInputWrapper,
  FileTypeWrapper,
  StyledInput,
  StyledSelect,
  StepIndicator,
  TreeContainer,
  SelectedCount,
  Step2ContentWrapper,
  LoadingBackdrop
} from './SaveToVaultModal.styled';
import VaultFolderTreeView from './components/VaultFolderTreeView';

const FILE_TYPES = [
  { value: 'txt', label: 'Text (.txt)', mimeType: 'text/plain' },
  { value: 'pdf', label: 'PDF (.pdf)', mimeType: 'application/pdf' },
  { value: 'docx', label: 'Word Document (.docx)', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }
];

const SaveToVaultModal = ({ isOpen, onClose, onSave, documentTitle }) => {
  const { authStore } = useStore();
  
  // Step 1 state
  const [fileName, setFileName] = useState(documentTitle);
  const [selectedFileType, setSelectedFileType] = useState('txt');
  
  // Step 2 state
  const [step, setStep] = useState(1);
  const [vaults, setVaults] = useState([]);
  const [expandedVaults, setExpandedVaults] = useState(new Set());
  const [vaultFolders, setVaultFolders] = useState({});
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [loadingVaults, setLoadingVaults] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState({});
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setFileName(documentTitle);
      setSelectedFileType('txt');
      setStep(1);
      setSelectedDestinations([]);
      setExpandedVaults(new Set());
      fetchVaults();
    }
  }, [isOpen, documentTitle]);

  // Fetch vaults from API (simple structure like Playground uses)
  const fetchVaults = async () => {
    setLoadingVaults(true);
    try {
      console.log('🔍 Fetching vaults with auth:', {
        hasToken: !!localStorage.getItem('authToken') || !!sessionStorage.getItem('authToken'),
        userId: authStore?.userId,
        tenantId: authStore?.currentWorkspaceUuid
      });
      
      // API requires scope at top level, meta nested (like DrivesStore)
      const baseMeta = {
        page: 1,
        page_size: 100,
        order_by: "created_at",
        order_direction: "DESC",
        filters: {}
      };
      
      console.log('📤 Fetching both USER and MANAGE scopes...');
      
      // Fetch BOTH USER and MANAGE scopes in parallel (like DrivesStore)
      const [userResp, manageResp] = await Promise.all([
        listDrives({ scope: "USER", meta: baseMeta }),
        listDrives({ scope: "MANAGE", meta: baseMeta })
      ]);
      
      console.log('✅ USER vaults:', userResp?.data);
      console.log('✅ MANAGE vaults:', manageResp?.data);
      
      // Parse and combine results
      const userVaults = userResp?.data?.body || userResp?.data?.data || [];
      const manageVaults = manageResp?.data?.body || manageResp?.data?.data || [];
      const vaultsData = [...userVaults, ...manageVaults];
      
      console.log('📊 Combined vaults:', {
        userCount: userVaults.length,
        manageCount: manageVaults.length,
        total: vaultsData.length
      });
      
      // Map to normalize vault properties
      const mapped = vaultsData.map((item) => ({
        id: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
        title: item.name || item.title || "Untitled Drive",
        name: item.name || item.title,
        vaultUuid: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
        description: item.description,
        scope: item.scope,
        folderCount: item.folderCount || item.folders_count
      }));
      
      console.log('📁 Total vaults loaded:', mapped.length);
      if (mapped.length > 0) {
        console.log('📋 Sample vault:', mapped[0]);
      }
      setVaults(mapped);
    } catch (error) {
      console.error('❌ Error fetching vaults:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setVaults([]);
    } finally {
      setLoadingVaults(false);
    }
  };

  // Fetch folders for a specific vault
  const fetchFoldersForVault = async (vaultUuid) => {
    setLoadingFolders(prev => ({ ...prev, [vaultUuid]: true }));
    try {
      console.log(`📂 Fetching folders for vault: ${vaultUuid}`);
      
      // Match My Workspace structure exactly (line 241-246)
      const foldersBody = {
        parent_folder_uuid: null,  // null = root level folders
        vault_uuid: vaultUuid,
        page: 1,
        page_size: 100,
        order_by: ["updated_at"],
        order_direction: ["DESC"],
        filters: {},
        cursor: null
      };
      
      console.log(`📤 Folders request body:`, foldersBody);
      
      const response = await listFoldersByTenant(foldersBody);
      
      console.log(`✅ Folders response for ${vaultUuid}:`, response.data);
      console.log(`📦 Response body:`, response.data?.body);
      
      const folders = response.data?.body || [];
      
      console.log(`📁 Loaded ${folders.length} folders for vault ${vaultUuid}`);
      if (folders.length > 0) {
        console.log(`📋 Sample folder:`, folders[0]);
      }
      
      setVaultFolders(prev => ({ ...prev, [vaultUuid]: folders }));
    } catch (error) {
      console.error(`❌ Error fetching folders for vault ${vaultUuid}:`, error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setVaultFolders(prev => ({ ...prev, [vaultUuid]: [] }));
    } finally {
      setLoadingFolders(prev => ({ ...prev, [vaultUuid]: false }));
    }
  };

  // Handle vault expansion
  const handleToggleVault = (vaultUuid) => {
    const newExpanded = new Set(expandedVaults);
    if (newExpanded.has(vaultUuid)) {
      newExpanded.delete(vaultUuid);
    } else {
      newExpanded.add(vaultUuid);
      // Fetch folders if not already loaded
      if (!vaultFolders[vaultUuid]) {
        fetchFoldersForVault(vaultUuid);
      }
    }
    setExpandedVaults(newExpanded);
  };

  // Handle destination selection
  const handleToggleDestination = (destination) => {
    setSelectedDestinations(prev => {
      const exists = prev.some(d => 
        d.vaultUuid === destination.vaultUuid && d.folderUuid === destination.folderUuid
      );
      
      if (exists) {
        return prev.filter(d => 
          !(d.vaultUuid === destination.vaultUuid && d.folderUuid === destination.folderUuid)
        );
      } else {
        return [...prev, destination];
      }
    });
  };

  if (!isOpen) return null;

  const handleNext = (e) => {
    e.stopPropagation();
    if (!fileName.trim()) return;
    setStep(2);
  };

  const handleBack = (e) => {
    e.stopPropagation();
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.stopPropagation();
    if (!fileName.trim() || selectedDestinations.length === 0) return;
    
    setIsSubmitting(true);
    try {
      const selectedType = FILE_TYPES.find(type => type.value === selectedFileType);
      await onSave(fileName.trim(), selectedType, selectedDestinations);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <ModalWrapper onClick={(e) => e.stopPropagation()}>
      <ModalContent role="dialog" onClick={(e) => e.stopPropagation()}>
        <StepIndicator>Step {step} of 2</StepIndicator>
        
        <ModalBody>
          {step === 1 ? (
          <HorizontalInputWrapper>
            <FlexInputWrapper>
              <InputLabel>File Name</InputLabel>
              <StyledInput
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Enter file name"
                disabled={isSubmitting}
                onClick={(e) => e.stopPropagation()}
              />
            </FlexInputWrapper>
            <FileTypeWrapper>
              <InputLabel>File Type</InputLabel>
              <StyledSelect
                value={selectedFileType}
                onChange={(e) => setSelectedFileType(e.target.value)}
                disabled={isSubmitting}
                onClick={(e) => e.stopPropagation()}
              >
                {FILE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </StyledSelect>
            </FileTypeWrapper>
          </HorizontalInputWrapper>
          ) : (
            <Step2ContentWrapper>
              <SelectedCount>
                {selectedDestinations.length} destination{selectedDestinations.length !== 1 ? 's' : ''} selected
              </SelectedCount>
              
              <TreeContainer>
                {loadingVaults ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>Loading vaults...</div>
                ) : (
                  <VaultFolderTreeView
                    vaults={vaults}
                    vaultFolders={vaultFolders}
                    expandedVaults={expandedVaults}
                    selectedDestinations={selectedDestinations}
                    loadingFolders={loadingFolders}
                    onToggleVault={handleToggleVault}
                    onToggleDestination={handleToggleDestination}
                  />
                )}
              </TreeContainer>
              {isSubmitting && (
              <LoadingBackdrop aria-hidden="true">
                <CircularProgress size="xl" />
              </LoadingBackdrop>
            )}
            </Step2ContentWrapper>
          )}
        </ModalBody>
        
        <ModalFooter>
          {step === 1 ? (
            <>
          <RoundedButton
            kind="default"
            outline
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </RoundedButton>
              <RoundedButton
                kind="blue"
                onClick={handleNext}
                disabled={!fileName.trim()}
              >
                Next →
              </RoundedButton>
            </>
          ) : (
            <>
              <RoundedButton
                kind="default"
                outline
                onClick={handleBack}
                disabled={isSubmitting}
              >
                ← Back
              </RoundedButton>
          <RoundedButton
            kind="blue"
            onClick={handleSubmit}
                disabled={isSubmitting || selectedDestinations.length === 0}
          >
                {isSubmitting ? 'Saving...' : `Save to ${selectedDestinations.length} location${selectedDestinations.length !== 1 ? 's' : ''}`}
          </RoundedButton>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalWrapper>
  );
};

SaveToVaultModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  documentTitle: PropTypes.string.isRequired,
};

export default SaveToVaultModal;